<script setup lang="ts">
import { computed } from 'vue'
import SearchInput from '@/components/ui/input/SearchInput.vue'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import BaseTabs from '@/components/ui/baseTab/BaseTabs.vue'
import BaseTabItem from '@/components/ui/baseTab/BaseTabItem.vue'
import { getKeywordTabUrl, getMercariUrl } from '@/lib/mercari'

const props = defineProps<{
	modelValue: string
	isSheetOpen: boolean
	placeholder: string
	keywordTabs?: { shopId: string; label: string; value: string }[]
	trendingKeywordSectionTitle?: string
	isSmall?: boolean
	isTabletPortrait?: boolean
}>()

const emit = defineEmits([
	'update:modelValue',
	'update:isSheetOpen',
	'search',
	'search-click',
	'close-sheet',
	'keyword-tab-click',
	'mercari-logo-click',
])

const isLocalSheetOpen = computed({
	get: () => props.isSheetOpen,
	set: value => emit('update:isSheetOpen', value),
})

const updateValue = (value: string) => {
	emit('update:modelValue', value)
}

const handleSearch = (value: string) => {
	emit('search', value)
}

const handleSearchClick = () => {
	emit('search-click')
}

const closeSheet = () => {
	emit('close-sheet')
}

const handleKeywordTabClick = (value: string) => {
	emit('keyword-tab-click', value)
}

const handleMercariLogoClick = () => {
	emit('mercari-logo-click')
}
</script>

<template>
	<section>
		<div class="bs-container overflow-hidden bg-o-75 lg:rounded-[8px]">
			<div
				class="relative col-span-12 py-20px mdp:pr-[11px] mdp:pt-24px mdl:pr-[43px] lg:pl-[30px] lg:pr-[143px]"
			>
				<div
					class="hero-desktop-left absolute left-[-33px] top-[0px] hidden h-[120px] w-[180px] bg-contain bg-center bg-no-repeat mdl:block lg:left-0"
				></div>

				<div
					class="hero-desktop-right absolute right-[-32px] top-[-9px] hidden h-[108px] w-[148px] bg-contain bg-center bg-no-repeat mdl:block lg:right-0 lg:top-[-2px] lg:h-[108px] lg:w-[167px]"
				></div>

				<div
					class="relative z-[1] flex gap-16px mdp:items-center mdp:gap-20px mdl:gap-[30px]"
				>
					<a class="flex items-center" :href="getMercariUrl('index')">
						<BaseIcon
							name="mercari_logo"
							class="h-[35px] w-[35px] mdl:hidden"
							@click="handleMercariLogoClick"
						/>

						<BaseIcon
							name="mercari_logo_text"
							class="hidden h-[35px] w-[138px] mdl:block"
							@click="handleMercariLogoClick"
						/>
					</a>

					<!-- Search input -->
					<div class="w-full">
						<!-- Desktop search input -->
						<div class="hidden mdp:block">
							<SearchInput
								:modelValue="modelValue"
								@update:modelValue="updateValue"
								:onSearch="handleSearch"
								:placeholder="placeholder"
								@click="handleSearchClick"
							/>
						</div>

						<!-- Mobile search input that triggers sheet -->
						<div class="block mdp:hidden">
							<Sheet v-model:open="isLocalSheetOpen">
								<SheetTrigger as-child>
									<div class="w-full">
										<SearchInput
											:modelValue="modelValue"
											@update:modelValue="updateValue"
											:onSearch="handleSearch"
											:placeholder="placeholder"
											@click="handleSearchClick"
										/>
									</div>
								</SheetTrigger>
								<SheetContent side="right" class="w-full">
									<div
										class="flex h-full flex-col gap-20px bg-white px-24px py-20px"
									>
										<div class="flex items-center gap-16px">
											<button
												@click="closeSheet"
												class="flex items-center bg-white"
											>
												<BaseIcon
													name="arrow_left_new"
													class="bs-icon flex h-24px w-24px items-center justify-center fill-n-950"
												></BaseIcon>
											</button>
											<div class="w-full">
												<SearchInput
													:modelValue="modelValue"
													@update:modelValue="updateValue"
													:onSearch="handleSearch"
													:placeholder="placeholder"
													@click="handleSearchClick"
												/>
											</div>
										</div>
										<div class="flex-1 bg-white">
											<section v-if="keywordTabs && keywordTabs.length > 0">
												<div
													class="col-span-12 pb-[10px] pt-20px mdp:pb-0 mdp:pt-[60px]"
												>
													<div
														class="mb-16px text-n-950 mdp:mb-casual-friends"
														:class="{
															'typography-s1-str': isSmall,
															'typography-h4-str': isTabletPortrait,
														}"
													>
														{{ trendingKeywordSectionTitle }}
													</div>

													<BaseTabs
														size="medium"
														type="secondary"
														class="flex-wrap"
													>
														<BaseTabItem
															v-for="tab of keywordTabs"
															:key="tab.label"
															type="secondary"
															size="medium"
															@click="handleKeywordTabClick(tab.value)"
														>
															{{ tab.label }}
														</BaseTabItem>
													</BaseTabs>
												</div>
											</section>
										</div>
									</div>
								</SheetContent>
							</Sheet>
						</div>
					</div>
				</div>

				<div
					class="mt-12px hidden min-h-[20px] items-center gap-24px mdp:ml-[78px] mdp:flex mdl:ml-[191px] lg:ml-[193px]"
				>
					<template v-if="keywordTabs && keywordTabs.length > 0">
						<a
							v-for="item of keywordTabs"
							:key="item.label"
							:href="getKeywordTabUrl(item.value, keywordTabs)"
							class="typography-c1-reg !text-n-800 !no-underline"
							@click="handleKeywordTabClick(item.value)"
						>
							{{ item.label }}
						</a>
					</template>
				</div>
			</div>
		</div>
	</section>
</template>
